import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { ExclamationTriangleIcon, EyeIcon, EyeSlashIcon, LockClosedIcon, GlobeAltIcon, InformationCircleIcon, ArrowPathIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { renderAsync } from 'docx-preview';

// hCaptcha site key
const HCAPTCHA_SITE_KEY = '43125db8-c85e-45fc-96ba-5f04c20c0e0c';

// Ellenőrizzük, hogy fejlesztői módban vagyunk-e
const isDevelopment = process.env.NODE_ENV === 'development';

const SharedContractView = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [searchParams] = useSearchParams();
  const shareId = searchParams.get('share');
  const { t, language, setLanguage } = useLanguage();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  const [documentData, setDocumentData] = useState<{
    name: string;
    type: string;
    fileUrl: string;
    fileName: string;
    viewCount?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  
  // Referenciák
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLObjectElement>(null);

  // Nyelvváltás kezelése
  const toggleLanguage = () => {
    setLanguage(language === 'hu' ? 'en' : 'hu');
    setLanguageMenuOpen(false);
  };

  // hCaptcha token változásakor nullázzuk a hibaüzenetet
  useEffect(() => {
    if (captchaToken) {
      setCaptchaError(null);
    }
  }, [captchaToken]);

  useEffect(() => {
    const validateShare = async () => {
      setLoading(true);
      setError(null);
      setDebugInfo(null);

      try {
        // Ellenőrizzük, hogy van-e megfelelő azonosító
        if (!contractId || !shareId) {
          setError(t('shared.invalidLinkMissingParams'));
          setDebugInfo(`contractId: ${contractId}, shareId: ${shareId}`);
          setLoading(false);
          return;
        }

        // Naplózzuk a paramétereket
        console.log("Cloud Function hívása:", { contractId, shareId });
        setDebugInfo(`${t('debug.parameters')}: contractId=${contractId}, shareId=${shareId}`);

        // Cloud Function meghívása jelszó nélkül, ellenőrizzük, hogy kell-e jelszó
        const validateContractShare = httpsCallable(functions, 'validateContractShare');
        const result = await validateContractShare({ 
          contractId, 
          shareId 
        });
        
        console.log("Cloud Function válasza:", result);
        
        const data = result.data as any;
        
        if (data.needsPassword) {
          // Jelszóra van szükség
          setNeedsPassword(true);
          setLoading(false);
        } else {
          // Nincs szükség jelszóra, megkaptuk a fájl adatait
          setDocumentData(data);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Hiba a megosztott dokumentum elérése során:', err);
        setError(err.message || t('shared.errorLoadingDocument'));
        
        // Részletesebb hiba információ
        if (err.code) {
          setDebugInfo(`${t('debug.errorCode')}: ${err.code}, ${t('debug.details')}: ${err.details || t('debug.noDetails')}`);
        } else {
          setDebugInfo(`${t('debug.error')}: ${JSON.stringify(err)}`);
        }
        
        setLoading(false);
      }
    };

    validateShare();
  }, [contractId, shareId, t]);

  // Fájltípus felismerése a fájlnév alapján, nem csak az URL végzodése alapján
  const getFileType = (url: string, fileName: string): string => {
    // Először a fájlnévből próbáljuk kinyerni a kiterjesztést
    const fileNameExt = fileName.split('.').pop()?.toLowerCase();
    if (fileNameExt) {
      return fileNameExt;
    }
    
    // Ha nincs fájlnév, akkor az URL-ből próbáljuk kinyerni
    // Firebase Storage URL-ben az alt=media előtt van a fájlnév
    const filePathMatch = url.match(/\/([^/?]+)\?/);
    if (filePathMatch && filePathMatch[1]) {
      const decodedFileName = decodeURIComponent(filePathMatch[1]);
      const ext = decodedFileName.split('.').pop()?.toLowerCase();
      if (ext) {
        return ext;
      }
    }
    
    // Végső megoldásként az URL végét nézzük
    return url.split('.').pop()?.toLowerCase() || 'unknown';
  };

  // Dokumentum megjelenítése
  const renderDocx = async (fileUrl: string) => {
    // Biztosítsuk, hogy a konténer létezik
    if (!docxContainerRef.current) {
      console.error('docxContainerRef.current nem létezik');
      setFileError(t('contracts.documentDisplayError'));
      return;
    }
    
    try {
      setFileLoading(true);
      setFileError(null);
      
      // Fájl letöltése
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`HTTP hiba: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Ellenőrizzük újra, hogy a konténer még mindig létezik-e
      if (!docxContainerRef.current) {
        throw new Error('A dokumentum konténer nem elérhető');
      }
      
      // Dokumentum megjelenítése docx-preview segítségével
      await renderAsync(blob, docxContainerRef.current, undefined, {
        className: 'docx-viewer',
        inWrapper: true,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        hideWrapperOnPrint: true,
        experimental: false,
        trimXmlDeclaration: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
        renderChanges: false,
        renderComments: false,
        renderAltChunks: true,
        debug: isDevelopment
      });
      
      setFileLoading(false);
    } catch (err: any) {
      console.error('Hiba a dokumentum megjelenítése során:', err);
      setFileError(err.message || t('contracts.documentDisplayError'));
      setFileLoading(false);
    }
  };

  // Dokumentum újratöltése
  const reloadDocument = () => {
    if (!documentData?.fileUrl) return;
    
    const fileType = getFileType(documentData.fileUrl, documentData.fileName || '');
    
    if (fileType === 'docx') {
      if (docxContainerRef.current) {
        try {
          // Biztonsági ellenőrzés
          docxContainerRef.current.innerHTML = '';
          renderDocx(documentData.fileUrl);
        } catch (err) {
          console.error('Hiba a dokumentum újratöltése során:', err);
          setFileError(t('contracts.documentDisplayError'));
        }
      } else {
        console.error('docxContainerRef.current nem létezik az újratöltésnél');
        setFileError(t('contracts.documentDisplayError'));
      }
    }
  };

  // Amikor a dokumentum adatok elérhetővé válnak, megjelenítjük a dokumentumot
  useEffect(() => {
    if (!documentData?.fileUrl) return;
    
    const fileType = getFileType(documentData.fileUrl, documentData.fileName || '');
    console.log("Fájl típusa:", fileType, "URL:", documentData.fileUrl, "Fájlnév:", documentData.fileName);
    
    // Késleltetett renderelés, hogy biztosan felépüljön a DOM
    const renderTimer = setTimeout(() => {
      if (fileType === 'docx') {
        renderDocx(documentData.fileUrl);
      } else {
        setFileLoading(false);
      }
    }, 100); // Rövid késleltetés
    
    return () => clearTimeout(renderTimer);
  }, [documentData]);

  // hCaptcha callback
  const onCaptchaVerify = (token: string) => {
    console.log('hCaptcha token:', token);
    setCaptchaToken(token);
  };

  const onCaptchaError = (err: any) => {
    console.error('hCaptcha hiba:', err);
    setCaptchaError(t('shared.captchaError'));
  };

  const onCaptchaExpire = () => {
    console.log('hCaptcha token lejárt');
    setCaptchaToken(null);
    setCaptchaError(t('shared.captchaExpired'));
  };

  // Jelszó ellenőrzése
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setCaptchaError(null);
    setDebugInfo(null);

    // Ellenőrizzük, hogy a captcha-t kitöltötték-e
    if (!captchaToken) {
      setCaptchaError(t('shared.captchaRequired'));
      return;
    }
    
    setLoading(true);
    
    try {
      // Cloud Function meghívása jelszóval és captcha tokennel
      console.log("Jelszó ellenőrzés:", { contractId, shareId, password: "***", captchaToken: "***" });
      
      const validateContractShare = httpsCallable(functions, 'validateContractShare');
      const result = await validateContractShare({ 
        contractId, 
        shareId,
        password,
        captchaToken
      });
      
      console.log("Jelszó ellenőrzés válasza:", result);
      
      const data = result.data as any;
      
      if (data.needsPassword) {
        // Valami hiba történt
        setPasswordError(t('shared.invalidPassword'));
        // Reseteljük a captcha-t
        captchaRef.current?.resetCaptcha();
        setCaptchaToken(null);
      } else {
        // Sikeres jelszó, megkaptuk a fájl adatait
        setDocumentData(data);
        setNeedsPassword(false);
      }
    } catch (err: any) {
      console.error('Hiba a jelszó ellenőrzése során:', err);
      
      // Captcha hiba kezelése
      if (err.message && err.message.includes('captcha')) {
        setCaptchaError(err.message || t('shared.captchaVerificationFailed'));
        captchaRef.current?.resetCaptcha();
        setCaptchaToken(null);
      } else {
        setPasswordError(err.message || t('shared.incorrectPasswordTryAgain'));
      }
      
      // Részletesebb hiba információ
      if (err.code) {
        setDebugInfo(`${t('debug.errorCode')}: ${err.code}, ${t('debug.details')}: ${err.details || t('debug.noDetails')}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Nyelvválasztó komponens
  const LanguageSelector = () => (
    <div className="flex items-center space-x-2">
      {isDevelopment && (
        <button 
          onClick={() => setShowDebugInfo(!showDebugInfo)} 
          className="flex items-center text-gray-300 hover:text-white transition-colors bg-gray-700 hover:bg-gray-600 rounded px-2 py-1 text-xs"
        >
          <InformationCircleIcon className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">{showDebugInfo ? t("debug.hideInfo") : t("debug.showInfo")}</span>
        </button>
      )}
      <div className="relative">
        <button 
          className="flex items-center text-gray-300 hover:text-white transition-colors" 
          onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
        >
          <GlobeAltIcon className="h-5 w-5 mr-1" />
          <span className="text-sm uppercase">{language}</span>
        </button>
        
        {languageMenuOpen && (
          <div className="absolute right-0 mt-2 py-2 w-32 bg-gray-800 rounded-md shadow-xl z-10">
            <button
              className={`block w-full text-left px-4 py-2 text-sm ${language === 'hu' ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
              onClick={() => setLanguage('hu')}
            >
              Magyar
            </button>
            <button
              className={`block w-full text-left px-4 py-2 text-sm ${language === 'en' ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
              onClick={() => setLanguage('en')}
            >
              English
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Betöltési állapot
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  // Hibaüzenet megjelenítése
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center max-w-md">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">{t("common.error")}</h1>
            <p className="text-gray-400">{error}</p>
            
            {isDevelopment && showDebugInfo && debugInfo && (
              <div className="mt-4 p-4 bg-gray-800 rounded-md text-left">
                <p className="text-xs text-gray-400 mb-2">{t("debug.information")}:</p>
                <pre className="text-xs text-gray-300 overflow-auto whitespace-pre-wrap">{debugInfo}</pre>
              </div>
            )}
            
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700"
            >
              {t("common.reload")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Jelszóbekérő űrlap, ha szükséges
  if (needsPassword) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
            <div className="flex justify-center mb-6">
              <div className="bg-primary-900/40 p-3 rounded-full">
                <LockClosedIcon className="h-8 w-8 text-primary-400" />
              </div>
            </div>
            
            <h1 className="text-xl font-bold text-white text-center mb-2">
              {t("shared.passwordProtected")}
            </h1>
            
            <p className="text-gray-400 text-center mb-6">
              {t("shared.enterPassword")}
            </p>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  {t("shared.password")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full p-2.5 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-2 text-sm text-red-400">{passwordError}</p>
                )}
              </div>
              
              {/* hCaptcha komponens */}
              <div className="my-4">
                <div className="flex justify-center">
                  <HCaptcha
                    sitekey={HCAPTCHA_SITE_KEY}
                    onVerify={onCaptchaVerify}
                    onError={onCaptchaError}
                    onExpire={onCaptchaExpire}
                    ref={captchaRef}
                    theme="light"
                    size="normal"
                    languageOverride={language}
                  />
                </div>
                {captchaError && (
                  <p className="mt-2 text-sm text-red-400 text-center">{captchaError}</p>
                )}
              </div>
              
              {isDevelopment && showDebugInfo && debugInfo && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600 overflow-auto max-h-24">
                  {debugInfo}
                </div>
              )}
              
              <button
                type="submit"
                className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <><span className="animate-spin mr-2">⟳</span> {t("common.loading")}</>
                ) : (
                  t("shared.viewDocument")
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Ha nincs dokumentum adat
  if (!documentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center max-w-md">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-800 mb-2">{t("common.error")}</h1>
            <p className="text-gray-600">{t("contracts.documentNotFound")}</p>
            
            {isDevelopment && showDebugInfo && debugInfo && (
              <div className="mt-4 p-4 bg-gray-100 rounded-md text-left">
                <p className="text-xs text-gray-600 mb-2">{t("debug.information")}:</p>
                <pre className="text-xs text-gray-700 overflow-auto whitespace-pre-wrap">{debugInfo}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Engedélyezett hozzáférés esetén a dokumentum megjelenítése
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Fejléc */}
      <header className="bg-white shadow-md p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{documentData.name}</h1>
            <div className="flex items-center space-x-2">
              <p className="text-gray-600 text-sm">{t("contracts.types." + documentData.type)}</p>
              {documentData.viewCount !== undefined && (
                <div className="flex items-center text-gray-600 text-xs ml-3">
                  <EyeIcon className="h-3.5 w-3.5 mr-1" />
                  <span>{t("contracts.viewCount", { count: documentData.viewCount })}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {documentData.fileUrl && (
              <a
                href={documentData.fileUrl}
                download={documentData.fileName || "document"}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors inline-flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t("shared.download")}
              </a>
            )}
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Dokumentum tartalma */}
      <div className="flex-1 container mx-auto p-4">
        {documentData.fileUrl ? (
          <div className="h-[80vh] bg-white rounded-lg overflow-hidden shadow-xl relative">
            {/* Betöltési állapot jelzése */}
            {fileLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
                <p className="text-gray-600 mb-4">{t("common.loading")}</p>
              </div>
            )}
            
            {/* Hiba jelzése */}
            {fileError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mb-4" />
                <h2 className="text-lg font-medium text-gray-800 mb-2">{t("contracts.documentLoadError")}</h2>
                <p className="text-gray-600 mb-4">{fileError}</p>
                <div className="flex space-x-4">
                  <button 
                    onClick={reloadDocument} 
                    className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    {t("common.reload")}
                  </button>
                  <a
                    href={documentData.fileUrl}
                    download={documentData.fileName || "document"}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors inline-flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t("shared.download")}
                  </a>
                </div>
              </div>
            )}
            
            {/* DOCX megjelenítő */}
            {getFileType(documentData.fileUrl, documentData.fileName || '') === 'docx' && (
              <div 
                ref={docxContainerRef} 
                className="w-full h-full bg-white overflow-auto"
                id="docx-container"
              ></div>
            )}
            
            {/* PDF megjelenítő */}
            {getFileType(documentData.fileUrl, documentData.fileName || '') === 'pdf' && (
              <object
                ref={pdfContainerRef}
                data={documentData.fileUrl}
                type="application/pdf"
                className="w-full h-full" 
              >
                <p className="text-center p-4 text-white">
                  {t("contracts.browserCannotDisplay")}
                </p>
              </object>
            )}
            
            {/* Nem támogatott fájlformátum - csak letöltési lehetőség */}
            {(getFileType(documentData.fileUrl, documentData.fileName || '') !== 'docx' && 
              getFileType(documentData.fileUrl, documentData.fileName || '') !== 'pdf') && (
              <div className="w-full h-full flex flex-col items-center justify-center bg-white">
                <DocumentArrowDownIcon className="h-16 w-16 text-gray-500 mb-4" />
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  {t("contracts.unsupportedFileFormat")}
                </h2>
                <p className="text-gray-600 mb-6 text-center max-w-md">
                  {t("contracts.cannotPreviewThisFormat")}
                </p>
                <a
                  href={documentData.fileUrl}
                  download={documentData.fileName || "document"}
                  className="px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors inline-flex items-center text-lg"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t("shared.download")}
                </a>
                <p className="text-gray-500 mt-4 text-sm">
                  {documentData.fileName}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-8 shadow-xl text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-gray-800 mb-2">
              {t("contracts.documentNotDisplayable")}
            </h2>
            <p className="text-gray-400">
              {t("contracts.fileNotFoundOrUnavailable")}
            </p>
          </div>
        )}
      </div>

      {/* Lábléc */}
      <footer className="bg-gray-800 p-4 shadow-inner">
        <div className="container mx-auto flex justify-between items-center">
          <div className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Energia Info CRM
          </div>
          <div className="flex items-center">
            <LanguageSelector />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SharedContractView; 